import pluralize from 'pluralize';
import { useDispatch, useSelector } from 'react-redux';
import React, { FC, useState, useEffect } from 'react';
import type { FieldValues, UseFormHandleSubmit } from 'react-hook-form';
import close from '../../../../assets/images/close.svg';
import { useHistory, useLocation } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { addToCart, removeItemFromConfig } from '../../../actions/v3Order';
import type { Slot as SlotConfig } from '../../../reducers/v3Order/slotState';
import formatUSD from '../../../utils/formatUSD';
import type { ImageIntentContext } from '../../../utils/imageIntentReporter';
import { isJpgImage } from '../../../utils/imageHelpers';
import { extraConfigProducts, getProductByLocation } from '../helpers/store';
import useScreenSize from '../hooks/screen';
import {
  ImageProtectionOverlay,
  useImageIntentFriction,
} from '../shared/protectedImage';
import { imageIntentGalleryOf } from '../shared/protectedImage/imageIntentGallery';
import { SlotSelectorWrapper } from './productConfigStyles';
import useImageOrientation from '../../../hooks/useImageOrientation';
import { getPosesForSlot, getRequiredPoseCountForSlot } from './utils';
import {
  selectBackgroundsSatisfied,
  selectConfigFlowPages,
  selectConfiguredChildProducts,
} from '../../../selectors';

interface SlotPose {
  url?: string;
  selected_crop?: { url: string };
}

interface SlotBackground {
  image_thumbnail: string;
}

interface ChildProduct {
  id?: number;
  name: string;
  price: number;
  image_thumbnail?: string;
}

interface ParentProduct {
  name: string;
  price: number;
  maximum_children_count: number;
  required_children_count?: number | null;
  additional_data?: { free_indices?: number[] } & Record<string, unknown>;
}

// The slices of the store this screen reads.
interface SlotSelectorState {
  shoot: {
    shoot?: { background_choice_mode?: string | null } | null;
    shootProducts: unknown[];
    suggestedProducts?: unknown[];
    additionalOfferProducts?: unknown[];
  };
  additionalData: {
    studentId?: number | string | null;
    studentPreviewImageUrl?: string | null;
    selectedStudent?: { galleries?: unknown } | null;
  };
  v3Order: { productConfig: Record<string, SlotConfig> };
}

type AppDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

const SlotWrapper = styled.div<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 5px;
  height: 125px;
  width: 125px;
  font-weight: 800;
  align-items: center;
  justify-content: center;
  position: relative;
  border: ${(props) => (props.selected ? '2px solid #479DE3' : 'initial')};
  color: ${(props) => (props.selected ? '#479DE3' : 'initial')};
  overflow: hidden;
`;

const Slots = styled.div`
  display: grid;
  grid-template-columns: min-content min-content;
  grid-auto-rows: min-content;
  gap: 10px;
  margin-top: 15px;
`;

const SlotProductImage = styled.img`
  height: 85px;
`;

const SlotImageWrapper = styled.div`
  height: 85px;
  width: 65px;
  position: relative;
`;

const CloseIcon = styled.img`
  height: 13px;
  width: 13px;
  position: absolute;
  right: 8px;
  top: 10px;
  cursor: pointer;
`;

const BackgroundProductImage = styled.img`
  height: 85px;
`;

/* Carries the positioning that used to live on PoseImage itself. An
   absolutely-positioned box with no width/height shrink-wraps its child, so
   this hugs the pose exactly and the protection overlay can never paint
   outside the photo — SlotImageWrapper is a fixed 65x85 frame the pose
   routinely overflows or under-fills, so it is not a safe overlay parent. */
const PoseImageBox = styled.div`
  position: absolute;
  bottom: 0px;
  left: 50%;
  transform: translateX(-50%);
  line-height: 0;
`;

const PoseImage = styled.img<{ is_horizontal: boolean }>`
  display: block;
  height: ${(props) => {
    return props.is_horizontal ? 'auto' : '85px';
  }};
  width: ${(props) => {
    return props.is_horizontal ? '85px' : 'auto';
  }};
`;

const ProductTitle = styled.div`
  font-size: 12px;
  margin-top: 2px;
  text-align: center;
  color: #000000;
`;

const SlotTitle = styled.div<{ free?: boolean }>`
  text-align: center;
  color: ${(props) => (props.free ? '#479DE3' : 'initial')};
`;

interface SlotProps {
  number: number;
  product?: ChildProduct;
  currentSlot: number;
  background?: SlotBackground;
  pose?: SlotPose;
  slotCount: number;
  freeIndices: number[];
  imageOrientations: Record<string, boolean>;
  imageUrl: string | null | undefined;
}

const Slot: FC<SlotProps> = ({
  number,
  product,
  currentSlot,
  background,
  pose,
  slotCount,
  freeIndices,
  imageOrientations,
  imageUrl,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();
  const location = useLocation();
  const { studentPreviewImageUrl } = useSelector(
    (state: SlotSelectorState) => state.additionalData,
  );
  // The gallery comes from the hook's Redux defaults (the order's gallery).
  const { getFrictionProps } = useImageIntentFriction({
    surface: 'product_config',
  });

  const [productName, setProductName] = useState<string | undefined>('');

  useEffect(() => {
    const _productName =
      product && product.name.length > 26
        ? product.name.substring(0, 26) + '...'
        : product?.name;
    setProductName(_productName);
  }, [product]);

  const onClickSlotWrapper = () => {
    if (location.hash !== '#slots') return;
    if (currentSlot === slotCount)
      return Swal.fire(`Please click on Done, Add to Cart.`);
    if (number !== currentSlot)
      return Swal.fire(
        `Please tap item ${currentSlot + 1} to begin configuring.`,
      );
    history.push(location.pathname);
  };

  const remove = (event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(removeItemFromConfig(number, history));
  };

  return (
    <SlotWrapper
      data-testid={`config-slot-${number}`}
      selected={currentSlot === number}
      onClick={onClickSlotWrapper}
    >
      {product && <CloseIcon onClick={remove} src={close} />}
      {!product && !freeIndices?.includes(number) && (
        <SlotTitle>Item {number + 1}</SlotTitle>
      )}
      {!product && freeIndices?.includes(number) && (
        <SlotTitle free>
          FREE
          <br />
          Item {number + 1}
        </SlotTitle>
      )}
      {product && !background && !pose && (
        <SlotProductImage src={product.image_thumbnail} />
      )}
      {(background || pose) &&
        (() => {
          const poseImageUrl = pose?.url || studentPreviewImageUrl;
          const isJpg = poseImageUrl && isJpgImage(poseImageUrl);
          const showPose = !!((pose || studentPreviewImageUrl) && poseImageUrl);
          const poseContext: ImageIntentContext = {
            surface: 'product_config',
            // The preview-image fallback is the same url in every slot, so key
            // on the slot index to keep each thumbnail in its own bucket.
            imageKey: `slot-${number}`,
            // A pooled photo reports its own gallery (a sibling's, or none for
            // a group photo); the preview fallback keeps the order's.
            ...imageIntentGalleryOf(pose),
          };

          return (
            <SlotImageWrapper>
              {/* For JPGs: don't show background underneath (pose has no transparency) */}
              {/* For PNGs: show background so pose can overlay with transparency */}
              {background && !isJpg && (
                <BackgroundProductImage
                  data-testid="config-slot-background"
                  src={background.image_thumbnail}
                />
              )}
              {/* Always show pose/preview image when available */}
              {showPose && poseImageUrl && (
                <PoseImageBox>
                  <PoseImage
                    src={poseImageUrl}
                    {...getFrictionProps(poseContext)}
                    is_horizontal={
                      imageUrl ? imageOrientations[imageUrl] || false : false
                    }
                  />
                  <ImageProtectionOverlay
                    context={poseContext}
                    imageSrc={poseImageUrl}
                  />
                </PoseImageBox>
              )}
            </SlotImageWrapper>
          );
        })()}
      {product && <ProductTitle>{productName}</ProductTitle>}
    </SlotWrapper>
  );
};

const ActionButton = styled.button`
  border: none;
  align-self: stretch;
  background: #479de3;
  height: 42px;
  padding: 10px;
  font-size: 14px;
  color: white;
  border-radius: 3px;
  font-weight: 800;
  margin-top: 30px;

  :disabled {
    background: gray;
    pointer-events: none;
    cursor: initial;
    opacity: 0.3;
  }
`;

const Instruction = styled.span`
  color: #479de3;
`;

interface SlotSelectorProps {
  actionButtonTitle?: string;
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  instruction?: string;
}

const SlotSelector: FC<SlotSelectorProps> = ({
  actionButtonTitle = 'Done, Add to Cart',
  handleSubmit,
  instruction = 'Select a Product',
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const shoot = useSelector((state: SlotSelectorState) => state.shoot);
  const additionalData = useSelector(
    (state: SlotSelectorState) => state.additionalData,
  );
  const productConfig = useSelector(
    (state: SlotSelectorState) => state.v3Order.productConfig,
  );
  const hasGalleries = !!useSelector(
    (state: SlotSelectorState) =>
      state.additionalData?.selectedStudent?.galleries,
  );

  const background_choice_mode = useSelector(
    (state: SlotSelectorState) => state.shoot?.shoot?.background_choice_mode,
  );
  const isGreeenScreenEvent =
    background_choice_mode === 'advanced_greenscreen_backgrounds_only';

  const product: ParentProduct = getProductByLocation(
    shoot?.shootProducts,
    location,
    extraConfigProducts(shoot),
  );
  const {
    name,
    price,
    maximum_children_count: slotCount,
    additional_data = {},
    additional_data: { free_indices = [] } = {},
    required_children_count,
  } = product;

  const products: ChildProduct[] = useSelector(selectConfiguredChildProducts);
  // Selected rather than received: ConfigScreen used to thread this down purely
  // so the completion gate could read it.
  const pages = useSelector(selectConfigFlowPages);
  // Two different questions, two different shapes.
  //
  // Which background belongs to which slot — must stay slot-keyed. Only children
  // that collect a background store one, so on a parent mixing skip-background
  // digital downloads with regular children the compacted list is shorter than
  // the slot rail, and indexing it by slot shifts a later child's background
  // onto an earlier one (ENG-2630). Same shape as posesBySlot below.
  const backgroundsBySlot: (SlotBackground | undefined)[] = Array.from(
    Array(slotCount),
  ).map((_, i) => productConfig[i]?.background);
  // Poses by slot (array of arrays) to support 0, 1, or 2 poses per slot
  const posesBySlot: SlotPose[][] = Array.from(Array(slotCount)).map((_, i) =>
    getPosesForSlot(productConfig[i]),
  );
  const poses = posesBySlot.flat();

  const imageUrls = [
    ...poses.map((pose) => {
      if (!pose) return null;
      return pose.selected_crop ? pose.selected_crop.url : pose.url;
    }),
  ].filter(Boolean) as string[];

  const { imageOrientations } = useImageOrientation(imageUrls || []) as {
    imageOrientations: Record<string, boolean>;
  };

  const isYouPickX = Object.keys(additional_data).length == 0;
  const isBYO = Object.keys(additional_data).length > 0;

  // Never below 1: required_children_count is an integer on every parent row
  // and defaults to 0, so taking it verbatim made this 0 for any package left
  // at the default. That enabled Add to Cart with nothing chosen and let
  // submitOrder's guard through, which is how an empty package reached the
  // cart in the first place (ENG-1683). A package always needs at least one
  // item; addToCart refuses zero regardless, so leaving the button live only
  // traded a disabled button for a dead-end alert.
  const minimumChildCount = Math.max(
    1,
    isYouPickX &&
      typeof required_children_count === 'number' &&
      Number.isInteger(required_children_count)
      ? required_children_count
      : isYouPickX
      ? slotCount
      : 1,
  );

  // Per-slot pose requirement: each slot must have its required number of poses (0, 1, or 2)
  const allPosesSatisfied = Array.from(Array(slotCount)).every((_, i) => {
    const config = productConfig[i];
    const required = getRequiredPoseCountForSlot(config, pages);
    return getPosesForSlot(config).length >= required;
  });
  // Asked per slot, not as a count — see selectBackgroundsSatisfied and the same
  // gate in configScreen.
  const allBackgroundsSatisfied = useSelector(selectBackgroundsSatisfied);

  const actionButtonEnabled =
    allPosesSatisfied &&
    allBackgroundsSatisfied &&
    products.length >= minimumChildCount;

  // hooks/screen.js is untyped JavaScript; it yields 'Mobile', 'Desktop', or
  // null before the first measurement.
  const [screenSize] = useScreenSize() as [string | null, boolean];

  const submitOrder = () => {
    handleSubmit((formState) => {
      if (isBYO) {
        if (products.length < minimumChildCount)
          return Swal.fire(
            `Please configure at least ${minimumChildCount} item(s).`,
          );

        if (!allBackgroundsSatisfied && isGreeenScreenEvent)
          return Swal.fire(`Please finish configuring all items.`);

        if (!allPosesSatisfied && hasGalleries) {
          return Swal.fire(`Please finish configuring all items.`);
        }
      }

      if (isYouPickX && !actionButtonEnabled) {
        return Swal.fire(
          `Please configure ${minimumChildCount} ${pluralize(
            'item',
            minimumChildCount,
          )}.`,
        );
      }

      const fullProduct = getProductByLocation(
        shoot.shootProducts,
        location,
        extraConfigProducts(shoot),
      );

      // Collect product options from all configured slots
      const allProductOptions: Record<
        string,
        {
          productId: unknown;
          productName: unknown;
          optionSetKey: string;
          optionValue: unknown;
        }
      > = {};

      // For You-Pick products, we need to check each slot's productOptions
      Object.values(productConfig).forEach((config, index) => {
        if (config.productOptions) {
          // Store options with a key that includes the slot index
          Object.entries(config.productOptions).forEach(
            ([optionSetKey, optionValue]: [string, unknown]) => {
              allProductOptions[`slot${index}_${optionSetKey}`] = {
                productId: config.product?.id,
                productName: config.product?.name,
                optionSetKey,
                optionValue,
              };
            },
          );
        }
      });

      dispatch(
        addToCart({
          studentId: additionalData.studentId,
          fullproductData: fullProduct,
          // A package's poses and backgrounds travel in productConfig, not
          // here. addToCart is untyped JavaScript, so its destructured keys
          // without defaults read as required; naming them undefined is what
          // leaving them out already passed.
          backgroundIds: undefined,
          backgroundSelections: undefined,
          poseIds: undefined,
          poses: undefined,
          legacyRequiredFields: undefined,
          productOptionKey: undefined,
          requiredFields: formState,
          productOptions: allProductOptions,
        }),
      );
    })();
  };

  let priceToDisplay = price;

  if (isBYO) {
    let freeIndices: number[] = [];

    if (additional_data?.free_indices) {
      freeIndices = additional_data.free_indices;
    }

    priceToDisplay = products.reduce((sum, product, index) => {
      if (!freeIndices.includes(index)) {
        return sum + product.price;
      }
      return sum;
    }, 0);
  }

  return (
    <SlotSelectorWrapper>
      {(screenSize === 'Desktop' || location.hash === '#slots') && (
        <h3 style={{ wordBreak: 'break-word', margin: '0 0 15px' }}>
          {name} <br />
          {formatUSD(priceToDisplay)} - <Instruction>{instruction}</Instruction>
        </h3>
      )}
      <Slots>
        {Array.from(Array(slotCount)).map((_, i) => (
          <Slot
            key={`slot-${i}`}
            freeIndices={free_indices}
            slotCount={slotCount}
            currentSlot={parseInt(
              (location.pathname.split('/').pop() ?? '').replace('#slots', ''),
            )}
            pose={
              posesBySlot[i]?.[0]?.selected_crop
                ? posesBySlot[i][0].selected_crop
                : posesBySlot[i]?.[0]
            }
            background={backgroundsBySlot[i]}
            product={products[i]}
            number={i}
            imageOrientations={imageOrientations}
            imageUrl={
              posesBySlot[i]?.[0]?.selected_crop
                ? posesBySlot[i][0].selected_crop?.url
                : posesBySlot[i]?.[0]?.url || null
            }
          />
        ))}
      </Slots>
      <ActionButton onClick={submitOrder}>{actionButtonTitle}</ActionButton>
    </SlotSelectorWrapper>
  );
};

export default SlotSelector;
